import { ENTITY_FIELD_TYPE, LEGO_CONSTANTS } from 'constants/index';
import { normalizeEntityListResponse, normalizeEntityResponse } from './apiResponseTransforms';
import { API, getPath } from './api';
// Import directly from the module, not the `utils` barrel: the barrel re-export `LegoTable`, which imports
// `RestService` from `services/lib`, which imports this file - going through the barrel would recreate that cycle.
import { CommonUtils } from 'utils/CommonUtils';
import { shouldUseTypeBasedEntityApi } from 'utils/entityIdentifier';

// Type-based paths are built on the same base marker as legacy paths (LEGO_CONSTANTS.COUI_API_PREFIX) so that
// getPath's existing replacePathWithProductSuffix substitution swaps in window.productSuffix for both uniformly -
// no separate token/replace step needed.
const V2_BASE = LEGO_CONSTANTS.COUI_API_PREFIX;
const V2_ENTITIES = LEGO_CONSTANTS.COUI_API_V2_ENTITIES_PATH;

// Same mechanism as the legacy `$id$` substitution — prefers explicit `type` when provided.
const idToType = ({ id, type, ...rest }) => ({ ...rest, type: type ?? id });

// Remove tenant property from payload as it's no longer required
const removeTenantFromPayload = payload => {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }
  const { tenant, ...rest } = payload;
  return rest;
};

// Transform entity payload for PUT request: remove type, set name from title if not present
// Also transform fields array: set field name from label if empty
const transformEntityPutPayload = payload => {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }
  const { type, ...rest } = payload;

  // If entity-level name is not present, derive it from title using the same normalization as FormBuildController.formatName
  if (!rest.name && rest.title) {
    rest.name = CommonUtils.formatIdentifierName(rest.title);
  }

  // Transform fields array: set field name from label if empty
  if (Array.isArray(rest.fields)) {
    rest.fields = rest.fields.map(field => {
      if (!field || typeof field !== 'object') {
        return field;
      }
      const { name, label, ...fieldRest } = field;
      // If name is empty (null, undefined, or empty string), use lowercase label
      const finalName = name || (label ? label.toLowerCase() : '');

      const transformedField = { ...fieldRest, name: finalName, label, visible: true };

      // Typebased API expects the RELATIONSHIP target keyed as related_entity_type, not related_entity_id
      if (transformedField.type === ENTITY_FIELD_TYPE.RELATIONSHIP && Object.prototype.hasOwnProperty.call(transformedField, 'related_entity_id')) {
        const { related_entity_id: relatedEntityId, ...fieldWithoutRelatedEntityId } = transformedField;
        return { ...fieldWithoutRelatedEntityId, related_entity_type: relatedEntityId };
      }

      return transformedField;
    });
  }

  return rest;
};

export const API_ROUTES = {
  ENTITY_NATIVE: {
    method: 'GET',
    legacy: { path: API.ENTITIES_NATIVE },
    typeBased: { path: `${V2_BASE}/v1/schemas/native` },
  },
  ENTITY_GET: {
    method: 'GET',
    legacy: { path: API.ENTITIES },
    typeBased: { path: `${V2_BASE}/${V2_ENTITIES}/type/$type$`, mapArgs: idToType, transformResponse: normalizeEntityResponse },
  },
  ENTITY_LIST: {
    method: 'GET',
    legacy: { path: API.ENTITIES_LIST },
    typeBased: { path: `${V2_BASE}/${V2_ENTITIES}`, transformResponse: normalizeEntityListResponse },
  },
  ENTITY_COUNT: {
    method: 'GET',
    legacy: { path: API.ENTITIES_COUNT },
    typeBased: { path: `${V2_BASE}/${V2_ENTITIES}/count` },
  },
  ENTITY_CREATE: {
    method: 'POST',
    legacy: { path: API.ENTITIES_LIST, transformRequest: removeTenantFromPayload },
    typeBased: { path: `${V2_BASE}/${V2_ENTITIES}`, transformRequest: removeTenantFromPayload, transformResponse: normalizeEntityResponse },
  },
  ENTITY_PUT: {
    method: 'PUT',
    legacy: { path: API.ENTITIES },
    typeBased: {
      path: `${V2_BASE}/${V2_ENTITIES}/type/$type$`,
      mapArgs: idToType,
      transformRequest: transformEntityPutPayload,
      transformResponse: normalizeEntityResponse,
    },
  },
  ENTITY_DELETE: {
    method: 'DELETE',
    legacy: { path: API.ENTITIES },
    typeBased: { path: `${V2_BASE}/${V2_ENTITIES}/type/$type$`, mapArgs: idToType },
  },
  ENTITY_RECORD_GET: {
    method: 'GET',
    legacy: { path: API.ENTITY_RECORDS },
    typeBased: { path: `${V2_BASE}/${V2_ENTITIES}/type/$type$/records/$displayId$`, mapArgs: idToType },
  },
  ENTITY_RECORDS_LIST: {
    method: 'GET',
    legacy: { path: API.ENTITY_RECORDS },
    typeBased: { path: `${V2_BASE}/${V2_ENTITIES}/type/$type$/records`, mapArgs: idToType },
  },
  ENTITY_RECORDS_COUNT: {
    method: 'GET',
    legacy: { path: API.ENTITY_RECORDS_COUNT },
    typeBased: { path: `${V2_BASE}/${V2_ENTITIES}/type/$type$/records/count`, mapArgs: idToType },
  },
  ENTITY_RECORDS_SEARCH: {
    method: 'GET',
    legacy: { path: API.ENTITY_RECORDS_SEARCH },
    typeBased: { path: `${V2_BASE}/${V2_ENTITIES}/type/$type$/records/search`, mapArgs: idToType },
  },
  ENTITY_RECORD_CREATE: {
    method: 'POST',
    legacy: { path: API.ENTITY_RECORDS, transformRequest: removeTenantFromPayload },
    typeBased: { path: `${V2_BASE}/${V2_ENTITIES}/type/$type$/records`, mapArgs: idToType, transformRequest: removeTenantFromPayload },
  },
  ENTITY_RECORD_PUT: {
    method: 'PUT',
    legacy: { path: API.ENTITY_RECORDS },
    typeBased: { path: `${V2_BASE}/${V2_ENTITIES}/type/$type$/records/$displayId$`, mapArgs: idToType },
  },
  ENTITY_RECORD_DELETE: {
    method: 'DELETE',
    legacy: { path: API.ENTITY_RECORDS },
    typeBased: { path: `${V2_BASE}/${V2_ENTITIES}/type/$type$/records/$displayId$`, mapArgs: idToType },
  },
  IMPORT_RECORDS_LIST: {
    method: 'GET',
    legacy: { path: API.IMPORT_RECORDS_LISTING },
    typeBased: { path: `${V2_BASE}/${V2_ENTITIES}/type/$type$/records/imports/list`, mapArgs: idToType },
  },
  IMPORT_RECORDS_UPLOAD: {
    method: 'POST',
    legacy: { path: API.IMPORT_RECORDS_UPLOAD },
    typeBased: { path: `${V2_BASE}/${V2_ENTITIES}/type/$type$/records/imports`, mapArgs: idToType },
  },
  IMPORT_RECORDS_MAPPING: {
    method: 'POST',
    legacy: { path: API.IMPORT_RECORDS_MAPPING },
    typeBased: { path: `${V2_BASE}/${V2_ENTITIES}/type/$type$/records/imports/$uploadId$/confirmed`, mapArgs: idToType },
  },
  IMPORT_RECORDS_STATUS: {
    method: 'GET',
    legacy: { path: API.IMPORT_RECORDS_PROGRESS },
    typeBased: { path: `${V2_BASE}/${V2_ENTITIES}/type/records/imports/$uploadId$/status` },
  },
  EXPORT_RECORDS_LIST: {
    method: 'GET',
    legacy: { path: API.EXPORT_RECORDS_LISTING },
    typeBased: { path: `${V2_BASE}/${V2_ENTITIES}/type/$type$/records/export/list`, mapArgs: idToType },
  },
  EXPORT_RECORDS_UPLOAD: {
    method: 'POST',
    legacy: { path: API.EXPORT_RECORDS_UPLOAD },
    typeBased: { path: `${V2_BASE}/${V2_ENTITIES}/type/$type$/records/export`, mapArgs: idToType },
  },
  EXPORT_RECORDS_PROGRESS: {
    method: 'GET',
    legacy: { path: API.EXPORT_RECORDS_PROGRESS },
    typeBased: {
      path: `${V2_BASE}/${V2_ENTITIES}/type/$type$/records/export/$jobId$/status`,
      mapArgs: idToType,
    },
  },
};

export const resolveRoute = (routeKey, ids = {}, params = {}) => {
  const route = API_ROUTES[routeKey];
  if (!route) throw new Error(`Unknown API route: ${routeKey}`);

  const variant = shouldUseTypeBasedEntityApi() ? route.typeBased : route.legacy;
  const mappedIds = variant.mapArgs ? variant.mapArgs(ids) : ids;

  // getPath already substitutes window.productSuffix for the V2_BASE/COUI_API_PREFIX marker (via
  // replacePathWithProductSuffix) for both legacy and type-based paths - no separate substitution needed here.
  return {
    url: getPath(variant.path, mappedIds, params),
    method: route.method,
    transformRequest: variant.transformRequest,
    transformResponse: variant.transformResponse,
  };
};
